import {course_getCourseById_Api,} from 'apis'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NullAsDefault} from 'views/Shared'
import {getId, getType} from "../../../../apis/model/base";
import {useCourseClassApi} from "./useCourseClassApi";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {ClassActionTypes} from "../index";
import {Modal} from "antd";
import {FullSideModalLayout} from "../../../../components/Modal/SideModalLayout";
import {bindingSearch} from "../../../Shared/components/PagerControls";
import CourseClassTeachers from "./CourseClassTeachers";
import {CourseClassSubjectList} from "./components/CourseClassSubjectForm";
import {nest, withProps} from "recompose";
import {CourseClassProvider} from "../CourseClassContext";
import {CourseClassStudentList} from "./CourseClassStudentForm";
import {CourseClassTimetables} from "./CourseClassTimetableForm";
import {CreateCourseClassModal, EditCourseClassModal} from "./CourseClassForm";
import {FetchClassById} from "../components/FetchEntity";
import LoadingBlock from "../../../LoadingBlock";
import _ from "lodash";

export const useCourseClassForm = ({
                                       course,
                                       autoRefresh = true,
                                   }) => {
    const t = useTranslate()
    const [refreshKey, setRefreshKey] = useState()
    const [localCourse, setLocalCourse] = useState(course)
    const onError = notifyOnError(t)
    const onRefresh = () => {
        if (autoRefresh) {
            setRefreshKey(Date.now())
        }
    }
    const owner_type = getType(course);
    const owner_id = getId(course);

    const {handleAsyncAction: getCourseById} = useAsyncAction({
        apiInfo: course_getCourseById_Api,
        onSuccess: (result, data) => {
            const dataItem = _.get(data, 'response.data');
            setLocalCourse(dataItem);
        },
    })

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const fetchCourseOnSuccess = () => {
        onCancel()
        getCourseById({}, {id: course?.id})
    }

    const {
        onCreate = NullAsDefault,
        onUpdate = NullAsDefault,
        onDelete = NullAsDefault,
    } = useCourseClassApi({
        onUpdated: onCancel,
        onCreated: fetchCourseOnSuccess,
        onDeleted: fetchCourseOnSuccess,
    })

    const confirmDelete = (id) => {
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                title: 'delete class of course',
                message: 'are you sure you want to delete this class of course',
                onOk: () => {
                    onDelete({}, {id})
                },
            })
        }
    }

    const handleAction = (action, params) => {
        const id = getId(params)
        switch (action) {
            case ClassActionTypes.CLASS_CREATE:
            case ClassActionTypes.CLASS_UPDATE:
            case ClassActionTypes.SHOW_TEACHER_LIST_MODAL:
            case ClassActionTypes.SHOW_SUBJECT_LIST_MODAL:
            case ClassActionTypes.SHOW_STUDENT_LIST_MODAL:
            case ClassActionTypes.SHOW_TIMETABLE_LIST_MODAL:
                setAction({type: action, value: params})
                break
            case ClassActionTypes.CLASS_DELETE:
                confirmDelete(id)
                break
            default:
                break
        }
    }

    const modal = useMemo(() => {
        if (!!!action?.type) return null;
        let params = {}
        let modal_params = {}
        let Component = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        const id = getId(action?.value)
        switch (action?.type) {
            case ClassActionTypes.SHOW_TEACHER_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('teachers of classes')}
                            </div>
                        }
                        onCancel={onCancel}>
                        <div className="p-3">
                            {bindingSearch({
                                onCancel,
                                owner_id,
                                owner_type,
                                item: action?.value,
                            })(CourseClassTeachers)}
                        </div>
                    </FullSideModalLayout>
                )
            case ClassActionTypes.SHOW_SUBJECT_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('subjects of classes')}
                            </div>
                        }
                        onCancel={onCancel}>
                        <div className="p-3">
                            {bindingSearch({
                                onCancel,
                                item: action?.value,
                            })(CourseClassSubjectList)}
                        </div>
                    </FullSideModalLayout>
                )
            case ClassActionTypes.SHOW_STUDENT_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('students of class')}
                            </div>
                        }
                        onCancel={onCancel}>
                        {bindingSearch({
                            onCancel,
                            owner_id,
                            owner_type,
                            item: action?.value,
                        })(nest(
                            withProps(({item}) => ({item: getId(item)}))(CourseClassProvider),
                            CourseClassStudentList
                        ))}
                    </FullSideModalLayout>
                )
            case ClassActionTypes.SHOW_TIMETABLE_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('timetable for the class')}
                            </div>
                        }
                        onCancel={onCancel}>
                        {bindingSearch({
                            onCancel,
                            owner_id,
                            owner_type,
                            item: action?.value,
                        })(nest(
                            withProps(({item}) => ({item: getId(item)}))(CourseClassProvider),
                            CourseClassTimetables
                        ))}
                    </FullSideModalLayout>
                )
            case ClassActionTypes.CLASS_CREATE:
                Component = CreateCourseClassModal
                modal_params = {
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('create class')}
                        </div>
                    ),
                }
                params = {
                    container: course,
                    onSubmit: (values) => {
                        onCreate(values, {id: course?.id})
                    },
                }
                break
            case ClassActionTypes.CLASS_UPDATE:
                Wrapper = withProps({id})(FetchClassById)
                Component = EditCourseClassModal
                modal_params = {
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('edit course class')}
                        </div>
                    ),
                }
                params = {
                    container: course,
                    onSubmit: (values) => {
                        onUpdate(values, {id})
                    },
                }
                break
            default:
                break
        }
        return (
            <Modal
                width={720}
                footer={null}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Component
                                {...params}
                                {...{item, onCancel}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, course?.id])

    return {
        modal,
        onCancel,
        handleAction,
        onUpdate,
        confirmDelete,
        refreshKey
    }
}
